import { useEffect, useState } from "react";
import { HiArrowSmLeft } from "react-icons/hi";
import { httpService } from "../../services/httpService";
import { Typography } from "@mui/material";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";

const SelectAddOnsDrawer = ({
  isOpen,
  onClose,
  onSave,
  serviceCategoryId,
  petType,
  petTypeId,
  selectedAddons = [],
  serviceTypeName = "",
  isFullWidth=false,
  drawerWidth=null,
}) => {
  const [addons, setAddons] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && selectedAddons) {
      const map = {};
      selectedAddons.forEach((addon) => {
        map[addon.addon_id] = {
          addon_id: addon.addon_id,
          addon_price: Number(addon.addon_price),
          name: addon.name,
          selected: true,
        };
      });
      setSelectedMap(map);
    }
  }, [isOpen, selectedAddons]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        setLoading(true);
        let apiUrl = `/adonservice/getadonservices`;
        if (petTypeId) apiUrl += `?petType=${petTypeId}`;
        const { data: responseData } = await httpService.get(apiUrl);
        setAddons(responseData || []);
      } catch (error) {
        console.error("Error fetching addons:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && petTypeId) fetchAddons();
  }, [isOpen, serviceCategoryId, petTypeId]);

  const toggleAddOn = (addon) => {
    setSelectedMap((prev) => {
      const updated = { ...prev };
      if (updated[addon._id]?.selected) {
        delete updated[addon._id];
      } else {
        updated[addon._id] = {
          addon_id: addon._id,
          addon_price: addon.price === "Free" ? 0 : parseFloat(Number(addon.price)),
          name: `${addon.name} (${addon.price === "Free" ? 0 : addon.price})`,
          selected: true,
        };
      }
      return updated;
    });
  };

  const handleSaveAddons = () => {
    const formattedAddons = Object.values(selectedMap).map(({ addon_id, addon_price, name }) => ({
      addon_id,
      addon_price,
      name,
    }));
    onSave(formattedAddons);
  };

  const handleClose = () => onClose();

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
        onClick={handleClose}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full ${!isFullWidth ? drawerWidth || "lg:w-2/5" : ""} bg-white shadow-xl transform transition-transform duration-300 ease-out flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors duration-200 mr-3"
          >
            <HiArrowSmLeft className="w-5 h-5 text-gray-600" />
          </button>
          <Typography variant="h6" fontWeight="bold">
            {serviceTypeName && serviceTypeName } Add-ons
          </Typography>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-24 space-y-3">
          {loading ? (
            <LoaderSpiner text="Loading..." />
          ) : addons.length === 0 ? (
            <Typography
              variant="body1"
              className="text-gray-500 text-center mt-10"
            >
              No add-ons added for this service category.
            </Typography>
          ) : (
            addons.map((addon) => {
              const selected = !!selectedMap[addon._id];
              const price =
                addon.price === "Free" ? "Free" : `Rs ${addon.price || 0}`;

              return (
                <div
                  key={addon._id}
                  className={`flex justify-between items-center p-3 rounded-lg shadow-sm border transition-transform duration-200 cursor-pointer ${
                    selected
                      ? "bg-indigo-50 border-indigo-200"
                      : "hover:bg-gray-50 hover:shadow-md"
                  }`}
                  onClick={() => toggleAddOn(addon)}
                >
                  <div className="flex items-center gap-3">
                    {/* Changed checkbox UI: square checkbox with check icon when selected */}
                    <div
                      className={`w-5 h-5 flex items-center justify-center border transition-all duration-300 rounded-sm ${
                        selected ? "bg-indigo-600 border-indigo-600" : "border-gray-300 bg-white"
                      }`}
                    >
                      {selected && (
                        <svg
                          className="w-3 h-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5 13l4 4L19 7"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-gray-800 font-medium">{addon.name}</span>
                  </div>
                  <span className="text-gray-700 font-semibold">{price}</span>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 bg-white px-4 py-3 border-t border-gray-200 shadow-md">
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={handleClose}
              className="w-full py-3 font-bold text-base border border-gray-300 rounded-xl hover:bg-gray-100"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAddons}
              className="w-full py-3 font-bold text-base bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
              disabled={loading}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectAddOnsDrawer;
